import { Color3, Constants, CubeMapToSphericalPolynomialTools, FromHalfFloat, RawCubeTexture, ThinEngine, Vector3, type Scene } from "@babylonjs/core";
import { daylightSky } from "./bakes.ts";
import { ENVIRONMENT_SIZE, LOD_SCALE, encodeSky } from "./sky-bake.ts";

/** PBR's diffuse lobe divides irradiance by π; IBL replaces most of the old hemi fill. */
export const DAY_SUN_INTENSITY = 4.5;
export const DAY_HEMI_INTENSITY = 0.08;
const ENVIRONMENT_INTENSITY = 0.5;

/**
 * Static sky lighting, baked on the CPU so context loss never races shader compilation
 * (`loadGameAssets` bakes the day sky off the main thread beforehand, see `sky-bake.ts`).
 * Babylon does not restore a hand-uploaded mip chain (and a cube created while the
 * context is lost is never rebuilt), so the cube is created from the retained CPU mips
 * only on a live context: now, and again after every restore.
 */
export function setupDaylightIBL(scene: Scene, zenith: Color3, horizon: Color3, sunDirection: Vector3): void {
  const engine = scene.getEngine();
  if (!(engine instanceof ThinEngine)) throw new Error("daylight IBL needs a WebGL engine");
  const caps = engine.getCaps();
  const hdr = !!(caps.textureHalfFloat && caps.textureHalfFloatLinearFiltering && caps.textureHalfFloatRender);
  const type = hdr ? Constants.TEXTURETYPE_HALF_FLOAT : Constants.TEXTURETYPE_UNSIGNED_BYTE;
  const sun = sunDirection.scale(-1).normalize();
  const sky = { zenith: [zenith.r, zenith.g, zenith.b], horizon: [horizon.r, horizon.g, horizon.b], sun: [sun.x, sun.y, sun.z] } as const;
  const levels = encodeSky(daylightSky(sky), hdr);
  // Diffuse irradiance from CPU data too: a GPU readback on a lost context would cache black.
  const faces = hdr ? levels[0]!.map((face) => Float32Array.from(face as Uint16Array, FromHalfFloat)) : levels[0]!;
  const polynomial = CubeMapToSphericalPolynomialTools.ConvertCubeMapToSphericalPolynomial({
    right: faces[0]!, left: faces[1]!, up: faces[2]!, down: faces[3]!, front: faces[4]!, back: faces[5]!,
    size: ENVIRONMENT_SIZE, format: Constants.TEXTUREFORMAT_RGBA,
    type: hdr ? Constants.TEXTURETYPE_FLOAT : Constants.TEXTURETYPE_UNSIGNED_BYTE, gammaSpace: false,
  }, 32);
  scene.environmentIntensity = ENVIRONMENT_INTENSITY;
  const create = (): void => {
    const texture = new RawCubeTexture(scene, levels[0]!, ENVIRONMENT_SIZE, Constants.TEXTUREFORMAT_RGBA, type, true, false);
    texture.name = "daylightIBL";
    texture.gammaSpace = false;
    texture.lodGenerationScale = LOD_SCALE;
    texture.lodGenerationOffset = 0;
    texture.sphericalPolynomial = polynomial;
    const internal = texture.getInternalTexture()!;
    // updateRawCubeTexture's level overload still uses base-level dimensions in 9.30;
    // updateTextureData fills the allocated mip chain with correctly sized faces.
    for (let lod = 1; lod < levels.length; lod++) {
      const size = ENVIRONMENT_SIZE >> lod;
      for (let face = 0; face < 6; face++) {
        engine.updateTextureData(internal, levels[lod]![face]!, 0, 0, size, size, face, lod, false);
      }
    }
    const previous = scene.environmentTexture;
    scene.environmentTexture = texture;
    previous?.dispose();
  };
  if (!engine._gl.isContextLost()) create();
  const restored = engine.onContextRestoredObservable.add(() => {
    if (!scene.isDisposed) create();
  });
  scene.onDisposeObservable.addOnce(() => engine.onContextRestoredObservable.remove(restored));
}
